// src/context/brandVoice/useBrandSaveActions.js — every WRITE the Brand Voice screen makes.
//
// It exists so BrandVoicePage stays what it should be: the header, the identity strip,
// the tabs and the panels. Everything that leaves the browser — the two-part save, the
// ledger's Freeze / Unlock, "Traduci ora", the "Risalva" of an unstamped path — lives
// here with the transient state those writes produce (`messages`, `problems`,
// `outcomes`, the local run lock).
//
// ⛔⛔ Three rules this file exists to keep in ONE place:
//   1. No sentence here is the server's. Every refusal is worded by `copy.js` from
//      `err.code` / `err.status`; ⛔ never `err.message`, never a `details.errors[].message`.
//   2. Only CHANGED paths are sent (`buildDocumentPayload`): the PUT stamps everything it
//      receives as the tenant's, so an unchanged field would lock the AI out for nothing.
//   3. The document and the voice are TWO writes with TWO etags and no transaction, so
//      `saveBrandIdentity` reports per part and so do the messages. ⛔ Never one verdict.
//   ⭐ v190: the voice is TWO of those writes (Italian, English), each with its own etag and
//   its own outcome; the browser checks cover the voice too (`voiceModel`).
//
// An AI update in progress (the read's `run_active`, or a 409 RUN_ACTIVE from any write) locks the
// screen. v191: the lock lifts BY ITSELF — the app-wide personalization poller (src/onboarding/)
// bumps a "run finished" epoch and the page re-reads; "Controlla di nuovo" (checkAgain) remains the
// manual fallback when that poller has no usable status.
// v199: `workspaceName` → the voice name, which is never shown and is filled in on every voice save, both languages
// (advancedModel.voiceLabelForSave).
import { useEffect, useRef, useState } from 'react';
import {
  ledgerPaths, lockFields, putBrandDocument, unlockFields,
} from '../brandIdentityApi.js';
import { putBrandVoice } from '../contextApi.js';
import { refreshEntitlements } from '../../hooks/useEntitlements.js';
import { validateField } from './validateField.js';
import { buildDocumentPayload } from './draftModel.js';
import {
  invalidPathsFrom, saveBrandIdentity, spentOnTranslation, unstampedPaths,
} from './saveBrandIdentity.js';
import { actionErrorCopy, saveErrorCopy } from './copy.js';
import { requestOnboardingRefresh } from '../../onboarding/onboardingStore.js';
import {
  italianFirstRefusal, voicePath, voicePayload, voiceProblems,
} from './voiceModel.js';
import { voiceLabelForSave } from './advancedModel.js';

const NONE = {};
const has = (o, k) => Object.prototype.hasOwnProperty.call(o || {}, k);
const isStale = (err) => err?.code === 'CONTEXT_ETAG_MISMATCH' || err?.status === 412;
const byPath = (response) => Object.fromEntries(
  (response?.outcomes || []).filter((o) => o?.field_path).map((o) => [o.field_path, o]),
);
// ⭐ v187: a problem found on the ENGLISH half of a bilingual field is tagged, so DocFields
// can mark the right column and FieldRow can say "(inglese)". validateField itself stays
// language-free.
const onEnglish = (p) => ({ ...p, params: { ...(p.params || {}), lang: 'en' } });
// The same label the rows show (⛔ the raw path only for a field this release
// does not know), because `actions.partial` names the field it is about.
const labelOf = (t, path) => t(`brandVoice.fields.${path}`, { defaultValue: path });
// ⭐ v190: a skipped voice half → OUR sentence, by language and reason.
const SKIPPED_KEYS = {
  it: { runActive: 'brandVoice.save.voiceSkipped' },
  en: { runActive: 'brandVoice.save.voiceEnSkipped', italianFirst: 'brandVoice.save.voiceEnSkippedItalian' },
};
const skippedKey = (lang, reason) => SKIPPED_KEYS[lang][reason] || SKIPPED_KEYS[lang].runActive;

/**
 * @param {object} arg
 * @param {Function} arg.t            the `context` namespace's translator
 * @param {string|null} arg.tenantId  the ACTIVE workspace
 * @param {object} arg.doc            useBrandIdentity's document part
 * @param {object} arg.voice          useBrandIdentity's voice part
 * @param {object} arg.voiceEn        useBrandIdentity's English voice part (v190)
 * @param {object} arg.draftApi       useBrandDraft's return
 * @param {Function} arg.reloadDoc    resolves the new document, or null
 * @param {Function} arg.reloadVoice  resolves the new voice, or null
 * @param {Function} arg.reloadVoiceEn resolves the new English voice, or null (v190)
 * @param {Function} [arg.onFocusPath] called with the first invalid path, so the page
 *   can open the tab that holds it (a problem on a hidden tab would be invisible)
 * @param {string} [arg.workspaceName] the active workspace's display name — the hidden voice name
 *   filled in on save ("<workspace> — voce" / "— voice")
 */
export default function useBrandSaveActions({
  t, tenantId, doc, voice, voiceEn, draftApi, reloadDoc, reloadVoice, reloadVoiceEn, onFocusPath,
  workspaceName,
}) {
  const [saving, setSaving] = useState(false);
  const [acting, setActing] = useState(false);
  const [runLockedLocal, setRunLockedLocal] = useState(false);
  const [messages, setMessages] = useState([]);
  const [problems, setProblems] = useState(NONE);
  const [outcomes, setOutcomes] = useState(NONE);

  // "Risalva" and the ledger actions run from a promise chain AFTER a re-read, so they
  // must see the FRESH document (its new etag and its newly stored values), never the
  // one captured when the button was rendered.
  const docRef = useRef(doc);
  docRef.current = doc;

  // Decision 3 (2026-09-27): the checks run only on SAVE, so a field's problems clear as
  // soon as the user edits THAT field — a row already fixed must not keep its red sentence
  // until the next click. When none is left, the bar's "some fields are not valid" line
  // (tagged `kind: 'invalid'`) goes with them; a 422 that named no field set no problem,
  // so its line is not tagged and stays.
  const clearProblems = (path) => setProblems((prev) => (has(prev, path)
    ? Object.fromEntries(Object.entries(prev).filter(([key]) => key !== path))
    : prev));
  useEffect(() => {
    if (Object.keys(problems).length > 0) return;
    setMessages((prev) => (prev.some((m) => m.kind === 'invalid') ? prev.filter((m) => m.kind !== 'invalid') : prev));
  }, [problems]);

  const noteRunLock = (err) => {
    if (err?.code !== 'RUN_ACTIVE') return;
    setRunLockedLocal(true);
    // v191: the app-wide personalization poller learns about the run at once (and polls every 10 s
    // until it ends — which is what lifts this lock by itself, see BrandVoicePage).
    requestOnboardingRefresh();
  };

  /** "Controlla di nuovo": one re-read, and the lock lifts only if the server says so. */
  const checkAgain = () => reloadDoc().then((data) => {
    if (data && !data.run_active) setRunLockedLocal(false);
    return data;
  });

  // ── Freeze / Unlock ─────────────────────────────────────────────────────────
  // ⚠️ They address the LEDGER's rows, so `verified_claims` sends its five categories
  // (`ledgerPaths`). A non-empty `skipped` is a WARNING, never a success: some row did
  // not move. ⛔ The rejection carries OUR sentence — ConfirmDialog renders it verbatim.
  const ledgerAction = (call) => (field) => {
    const path = field.field_path;
    setActing(true);
    return call({ tenant: tenantId, fieldPaths: ledgerPaths(path) })
      .then((res) => {
        const skipped = res?.skipped || [];
        // The messages are REPLACED, not appended: one about the previous state is stale
        // the moment the state moves.
        setMessages(skipped.length
          ? [{ tone: 'warning', text: t('brandVoice.actions.partial', { field: labelOf(t, path) }) }]
          : []);
        return reloadDoc();
      })
      .catch((err) => {
        noteRunLock(err);
        throw new Error(actionErrorCopy(t, err));
      })
      .finally(() => setActing(false));
  };

  // ── "Traduci ora" ───────────────────────────────────────────────────────────
  // Re-PUT the STORED Italian value with `translate: true` — the only way to ask for a
  // fresh English half on its own.
  // ⚠️ On `terminology.preferred` this persists a folded `instead_of` as `note`: the read
  // folds the AI's `instead_of` into `note`, and what we send back is what we were given.
  // Documented behaviour (plan decision 4), not a defect to "fix" here.
  const onTranslate = (field) => {
    const path = field.field_path;
    setActing(true);
    return putBrandDocument({
      tenant: tenantId, etag: docRef.current?.data?.etag, fields: { [path]: field.value }, translate: true,
    })
      .then((res) => {
        setOutcomes((prev) => ({ ...prev, ...byPath(res) }));
        if (spentOnTranslation(res)) refreshEntitlements();
        setMessages([]);
        return reloadDoc();
      })
      .catch((err) => {
        noteRunLock(err);
        setMessages([{ tone: 'error', text: actionErrorCopy(t, err) }]);
        return null;
      })
      .finally(() => setActing(false));
  };

  const unstampedMessage = (paths) => ({
    tone: 'warning',
    text: t('brandVoice.save.unstamped'),
    action: { label: t('brandVoice.save.resave'), onClick: () => resave(paths) },
  });

  // ── "Risalva": the paths the server wrote but did NOT record as the tenant's ──
  // ⭐ It FREEZES them (POST /document/lock) instead of saving again (decision 5, 2026-09-27):
  // a save stamps a field only when it actually WRITES it, so re-sending a CLEARED field —
  // already empty on the server — writes nothing and could never record its owner (and a
  // cleared usp/uvp reads back null, which a PUT refuses). The lock stamps the Italian row
  // `human` without touching the text: no etag, no translation, no credits — and the AI
  // decides on the Italian row alone (backend `apply.writable_fields`), so it is full
  // protection. v196: claims never reach here — `unstampedPaths` skips them, a run never
  // writes claims.
  async function resave(paths) {
    const fieldPaths = paths.flatMap(ledgerPaths);
    if (!fieldPaths.length) return;
    // `acting`, not `saving`: the save bar keeps its label while the lock runs.
    setActing(true);
    try {
      const res = await lockFields({ tenant: tenantId, fieldPaths });
      const skipped = new Set(res?.skipped || []);
      const still = paths.filter((path) => ledgerPaths(path).some((row) => skipped.has(row)));
      setMessages(still.length
        ? [unstampedMessage(still)]
        : [{ tone: 'success', text: t('brandVoice.save.resaved') }]);
      await reloadDoc();
    } catch (err) {
      noteRunLock(err);
      setMessages([{ tone: 'error', text: actionErrorCopy(t, err) }]);
    } finally {
      setActing(false);
    }
  }

  // ── "Salva modifiche" ───────────────────────────────────────────────────────
  const onSave = async () => {
    const fields = doc.data?.fields;
    const { draft, dirty, voiceIsDirty, voiceEnIsDirty } = draftApi;
    const known = new Map((fields || []).map((f) => [f.field_path, f]));

    // (1) Our own mirror of the backend's rules first: a 422 costs a round trip and
    // arrives worded in English we may not render.
    const found = {};
    dirty.forEach((path) => {
      const field = known.get(path);
      if (!field) return;
      const list = [
        ...(has(draft.fields, path) ? validateField(field, draft.fields[path]) : []),
        ...(field.policy === 'bucket_d' && has(draft.fieldsEn, path)
          ? validateField(field, draft.fieldsEn[path]).map(onEnglish) : []),
      ];
      if (list.length) found[path] = list;
    });
    // The voice name is never shown (a workspace has ONE personality) but the backend requires it: fill it in for
    // BOTH languages before the checks and the PUTs — an empty name, or the platform's own on a voice still read
    // from the platform, becomes "<workspace> — voce" / "— voice" (advancedModel.voiceLabelForSave).
    const nameFallback = (key) => (workspaceName ? t(key, { name: workspaceName }) : t('brandVoice.fields.voice'));
    const named = (value, read, key) => (value
      ? { ...value, label: voiceLabelForSave(value.label, read, nameFallback(key)) } : value);
    const itVoice = named(draftApi.voiceValue('it'), voice.data, 'brandVoice.voice.workspaceLabel');
    const enVoice = named(draftApi.voiceValue('en'), voiceEn.data, 'brandVoice.voice.workspaceLabelEn');
    // ⭐ v190 (decision 9): every shown axis, per language, keyed `voice.<lang>.<key>` — so the page opens the voice
    // tab AND the right language.
    if (voiceIsDirty) Object.assign(found, voiceProblems('it', itVoice));
    if (voiceEnIsDirty) Object.assign(found, voiceProblems('en', enVoice));
    const invalid = Object.keys(found);
    if (invalid.length) {
      setProblems(found);
      setMessages([{ tone: 'error', kind: 'invalid', text: t('brandVoice.save.errors.docInvalid') }]);
      onFocusPath?.(invalid[0]);
      return;
    }

    // ⭐ v190 (decision B): an English change alone, on a workspace still reading the platform's
    // voice, would be refused by the backend (no English file without the Italian one) — so it
    // is refused HERE and nothing is sent, the document included.
    if (italianFirstRefusal({ itDirty: voiceIsDirty, enDirty: voiceEnIsDirty, itSource: voice.data?.source })) {
      setProblems(NONE);
      setMessages([{ tone: 'error', text: t('brandVoice.voice.enNeedsItalian') }]);
      onFocusPath?.(voicePath('en', 'label'));
      return;
    }

    setProblems(NONE);
    setMessages([]);
    setSaving(true);
    const payload = buildDocumentPayload(fields, draft);
    const savedPaths = [...new Set([...Object.keys(payload.fields), ...Object.keys(payload.fieldsEn)])];
    try {
      const result = await saveBrandIdentity({
        api: { putBrandDocument, putBrandVoice },
        tenant: tenantId,
        doc: { etag: doc.data?.etag, ...payload },
        voice: voiceIsDirty ? { etag: voice.data?.etag, ...voicePayload(itVoice) } : null,
        voiceEn: voiceEnIsDirty ? { etag: voiceEn.data?.etag, ...voicePayload(enVoice) } : null,
        itOwned: voice.data?.source !== 'global',
        // v200: every save translates — there is no "Aggiorna anche l'inglese" switch any more.
        translate: true,
      });
      const next = [];
      const reread = { doc: false, voice: false, voiceEn: false };
      // ⭐ The parts are reported separately, in the order they were attempted.
      if (result.doc.status === 'saved') {
        const res = result.doc.response;
        setOutcomes(byPath(res));
        draftApi.dropSaved(savedPaths);
        next.push({ tone: 'success', text: t('brandVoice.save.docSaved') });
        const unstamped = unstampedPaths(res, fields);
        if (unstamped.length) next.push(unstampedMessage(unstamped));
        if (spentOnTranslation(res)) refreshEntitlements();
        reread.doc = true;
      } else if (result.doc.status === 'failed') {
        next.push(docFailure(result.doc.error, fields));
      }
      [['it', result.voice], ['en', result.voiceEn]].forEach(([lang, part]) => {
        const isEn = lang === 'en';
        if (part.status === 'saved') {
          draftApi.dropSaved([], isEn ? { voiceEn: true } : { voice: true });
          next.push({ tone: 'success', text: t(isEn ? 'brandVoice.save.voiceEnSaved' : 'brandVoice.save.voiceSaved') });
          // ⚠️ ALL THREE reads: the voice's lock state lives in the document read, and a first
          // Italian save turns the English read from the platform's voice into "no English voice".
          Object.assign(reread, { doc: true, voice: true, voiceEn: true });
        } else if (part.status === 'skipped') {
          next.push({ tone: 'warning', text: t(skippedKey(lang, part.reason)) });
        } else if (part.status === 'failed') {
          next.push(voiceFailure(part.error, lang));
        }
      });
      // ONE round of re-reads, whatever was saved.
      const [freshDoc] = await Promise.all([
        reread.doc ? reloadDoc() : null,
        reread.voice ? reloadVoice() : null,
        reread.voiceEn ? reloadVoiceEn() : null,
      ]);
      // ⭐ v190: the voice PUT stamps the voice as the workspace's own, but its answer does not say
      // whether it did — the document re-read does. Not `human` after a save → the same warning as
      // an unstamped field, and "Risalva" freezes `['voice']` (ledgerPaths('voice')).
      const lock = freshDoc?.voice;
      if (result.voice.status === 'saved' && lock?.present && lock.state !== 'human') {
        next.push(unstampedMessage(['voice']));
      }
      setMessages(next);
    } finally {
      setSaving(false);
    }
  };

  // A refused voice half: our sentence, and on a 412 "Ricarica", which re-reads THAT language,
  // keeps the draft and marks the language when its stored voice moved underneath it (decision
  // 6's rule). ⛔ A re-read that failed (null) proves nothing and marks nothing.
  function voiceFailure(err, lang) {
    const isEn = lang === 'en';
    const before = (isEn ? voiceEn : voice).data;
    const reload = isEn ? reloadVoiceEn : reloadVoice;
    const message = { tone: 'error', text: saveErrorCopy(t, err, isEn ? 'voiceEn' : 'voice') };
    if (isStale(err)) {
      message.action = {
        label: t('brandVoice.reload'),
        onClick: () => reload().then((data) => { if (data) draftApi.noteVoiceReload(lang, before, data); }),
      };
    }
    return message;
  }

  // A refused document part: our sentence, plus the ONE remedy that refusal has.
  function docFailure(err, fields) {
    noteRunLock(err);
    const message = { tone: 'error', text: saveErrorCopy(t, err, 'doc') };
    if (isStale(err)) {
      // "Ricarica" keeps the drafts (decision 6) and marks the fields whose STORED value
      // moved underneath one, so the user knows their text will replace someone else's.
      message.action = {
        label: t('brandVoice.reload'),
        onClick: () => reloadDoc().then((data) => draftApi.noteReload(fields, data?.fields)),
      };
    }
    if (err?.code === 'BRAND_GUIDELINES_FIELD_INVALID') {
      // ⛔ Only the `field_path` of each entry is read: its `message` is the server's English.
      // ⛔ Never tagged `lang: 'en'` (unlike a client check): the backend reports an English
      // half's errors under the ITALIAN `field_path`, so the wire cannot say which language.
      const paths = [...invalidPathsFrom(err)];
      if (paths.length) {
        setProblems(Object.fromEntries(paths.map((p) => [p, [{ key: 'invalidServer' }]])));
        message.kind = 'invalid';
      }
    }
    return message;
  }

  const onCancel = () => {
    draftApi.discard();
    setProblems(NONE);
    setMessages([]);
  };

  return {
    saving,
    busy: saving || acting,
    runLockedLocal,
    checkAgain,
    messages,
    problems,
    clearProblems,
    outcomes,
    onFreeze: ledgerAction(lockFields),
    onUnlock: ledgerAction(unlockFields),
    onTranslate,
    onSave,
    onCancel,
  };
}
